import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import AnswerPerformanceView from 'models/location/answer_performance_view';
import AssignWindowLocation from 'actions/lib/assign_window_location';
import connect from 'components/lib/connect';
import GuidePerformanceFilters from 'components/guide_performance/guide_performance_filters';
import GuidePerformanceView from 'models/location/guide_performance_view';
import { GuideType } from 'models/guide_performance';
import InteractiveTable from 'components/common/interactive_table';
import NavigateTo from 'actions/current_location/navigate_to';
import { Number } from 'components/common/number';
import PerformanceDateRange from 'models/performance_date_range';
import theme from 'scripts/presentation/themes/default';

export function GuidePerformanceTable({
  channel,
  data,
  dateRange,
  initialSort,
  isLoading,
  navigateToAnswerPerformance,
  navigateToEditTransactionGuide,
  navigateToSidekickConversationList,
  onSort,
  onPageNumberUpdate,
  pageNumber,
}) {
  const transformedData = data?.map(row => ({
    id: row.id,
    collectionId: row.collectionId,
    name: <NameCell>{row.name}</NameCell>,
    collectionName: <NameCell>{row.collectionName}</NameCell>,
    references: <Number value={row.references} />,
    handoffs: <Number value={row.handoffs} />,
    resolved: <Number value={row.resolved} />,
    resolutionRate: (
      <span>
        <Number value={row.resolutionRate} />%
      </span>
    ),
  }));

  const onNameCellClick = row => {
    const rowData = data.find(d => d.id === row.id);
    const rowType = rowData.type;

    if (rowType === GuideType.ANSWER_GUIDE) {
      navigateToAnswerPerformance(dateRange);
    }

    if (rowType === GuideType.TRANSACTION_GUIDE) {
      navigateToEditTransactionGuide(rowData.collectionId, rowData.id);
    }
  };

  const onNavigateToSidekickConversationList = (row, columnType) => {
    navigateToSidekickConversationList(row, channel, columnType);
  };

  const columns = [
    {
      header: 'Guide Name',
      accessor: 'name',
      align: 'left',
      minWidth: '250px',
      maxWidth: '500px',
      padding: `${theme.spacing.medium} ${theme.spacing.large} ${theme.spacing.medium} ${theme.spacing.medium}`,
      width: 'max-content',
      sortValue: item => data.find(d => d.id === item.id).name,
      isCellClickable: () => true,
      onCellClick: onNameCellClick,
    },
    {
      header: 'Sidekick Profile',
      accessor: 'collectionName',
      align: 'left',
      minWidth: '150px',
      width: '1fr',
      sortValue: item => data.find(d => d.id === item.id).collectionName,
    },
    {
      header: 'Count',
      accessor: 'references',
      align: 'right',
      padding: `${theme.spacing.medium} ${theme.spacing.large}`,
      width: 'max-content',
      sortValue: item => data.find(d => d.id === item.id).references,
      sortDescendingFirst: true,
      isCellClickable: () => true,
      onCellClick: row => onNavigateToSidekickConversationList(row, 'references'),
    },
    {
      header: 'Handed off',
      accessor: 'handoffs',
      align: 'right',
      padding: `${theme.spacing.medium} ${theme.spacing.large}`,
      width: 'max-content',
      sortValue: item => data.find(d => d.id === item.id).handoffs,
      sortDescendingFirst: true,
      isCellClickable: () => true,
      onCellClick: row => onNavigateToSidekickConversationList(row, 'handoffs'),
    },
    {
      header: 'Resolved',
      accessor: 'resolved',
      align: 'right',
      padding: `${theme.spacing.medium} ${theme.spacing.large}`,
      width: 'max-content',
      sortValue: item => data.find(d => d.id === item.id).resolved,
      sortDescendingFirst: true,
      isCellClickable: () => true,
      onCellClick: row => onNavigateToSidekickConversationList(row, 'resolved'),
    },
    {
      header: 'Resolution rate',
      accessor: 'resolutionRate',
      align: 'right',
      padding: `${theme.spacing.medium} ${theme.spacing.large}`,
      width: 'max-content',
      sortValue: item => data.find(d => d.id === item.id).resolutionRate,
      sortDescendingFirst: true,
    },
  ];

  return (
    <InteractiveTable
      columns={columns}
      controlComponents={<GuidePerformanceFilters />}
      data={transformedData || []}
      enableSearch
      enableSorting
      initialSort={initialSort?.key ? initialSort : { key: 'references' }}
      isLoading={isLoading}
      onPageNumberUpdate={onPageNumberUpdate}
      onSortConfigUpdate={onSort}
      pageNumber={pageNumber}
      rowsPerPage={25}
    />
  );
}

GuidePerformanceTable.propTypes = {
  channel: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      collectionId: PropTypes.string.isRequired,
      collectionName: PropTypes.string.isRequired,
      references: PropTypes.number.isRequired,
      handoffs: PropTypes.number.isRequired,
      resolved: PropTypes.number.isRequired,
      resolutionRate: PropTypes.number.isRequired,
    })
  ),
  dateRange: PropTypes.oneOf(Object.values(PerformanceDateRange)),
  isLoading: PropTypes.bool,
  initialSort: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']),
  }),
  navigateToAnswerPerformance: PropTypes.func,
  navigateToEditTransactionGuide: PropTypes.func,
  navigateToSidekickConversationList: PropTypes.func,
  onSort: PropTypes.func,
  pageNumber: PropTypes.number,
  onPageNumberUpdate: PropTypes.func,
  currentLocation: PropTypes.instanceOf(GuidePerformanceView).isRequired,
};

const NameCell = styled.div`
  align-items: center;
  display: flex;
  gap: ${p => p.theme.spacing.small};
`;

function mapStateToProps({ getProvider }) {
  const guidePerformancesProvider = getProvider('guidePerformances');
  const currentLocation = getProvider('currentLocation').get();

  return {
    channel: currentLocation.interactionType,
    data: guidePerformancesProvider.findAll(),
    dateRange: currentLocation.dateRange,
    isLoading: guidePerformancesProvider.isLoading(),
    initialSort: currentLocation.sort,
    pageNumber: currentLocation.pageNumber || 1,
    currentLocation,
  };
}

function mapExecuteToProps(executeAction, { currentLocation }) {
  return {
    navigateToAnswerPerformance: dateRange => {
      executeAction(NavigateTo, AnswerPerformanceView.create({ dateRange }));
    },
    navigateToSidekickConversationList: ({ id, collectionId }, channel, columnType) => {
      const params = {
        'f.ss': 'ENDED',
        'f.gcid': collectionId,
        'f.gids': id,
        ...(channel && { 'f.c': channel }),
        ...(columnType === 'handoffs' && { 'f.as': 'HANDED_OFF' }),
        ...(columnType === 'resolved' && { 'f.as': 'RESOLVED' }),
      };

      return executeAction(
        AssignWindowLocation,
        `/sidekick/communication-sessions?${new URLSearchParams(params).toString()}`
      );
    },
    navigateToEditTransactionGuide: (guideCollectionId, guideId) => {
      executeAction(AssignWindowLocation, `/sidekick/guides/${guideCollectionId}/txguides/${guideId}`);
    },
    onSort: sortConfig => {
      executeAction(NavigateTo, currentLocation.deriveNew({ sort: sortConfig }));
    },
    onPageNumberUpdate: pageNumber => {
      executeAction(NavigateTo, currentLocation.deriveNew({ pageNumber }));
    },
  };
}

const GuidePerformanceTableContainer = connect(mapStateToProps, mapExecuteToProps)(GuidePerformanceTable);

export default GuidePerformanceTableContainer;
